import type { BackendStatus, PaymentMode } from "./types"

export const STATUS_FILTER_ALL = "all" as const
export const COURSE_FILTER_ALL = "all" as const
export const PAYMENT_MODE_FILTER_ALL = "all" as const
export const CURRENCY_FILTER_ALL = "all" as const

/**
 * Selected currency on the invoice list filter: a lowercase ISO-4217 code,
 * or the "all" sentinel. Single-select — a list mixing currencies mixes
 * incomparable amounts, so exactly one choice is the meaningful one.
 *
 * The market filter has no sentinel: it is a `string[]` that is naturally
 * empty when unconstrained (design D9).
 *
 * Unlike `StatusFilterValue` / `PaymentModeFilterValue`, whose second arm
 * is a closed union, this widens to plain `string` — currency codes are
 * free text here (an unknown code round-trips rather than being dropped),
 * so the alias is documentation, not a constraint.
 */
export type CurrencyFilterValue = typeof CURRENCY_FILTER_ALL | string

export const SCOPE_ALL = "all" as const
export const SCOPE_MINE = "me" as const
/**
 * Selected owner on the invoice list filter.
 *
 * - ``"all"``: do not constrain by creator.
 * - ``"me"``: the signed-in user's own invoices.
 * - ``number``: a specific creator's primary key (Manager + Finance only).
 *
 * Sales Reps are server-side scoped to their own invoices regardless
 * of what is sent, so on their side the picker is hidden entirely.
 */
export type ScopeFilter = typeof SCOPE_ALL | typeof SCOPE_MINE | number

export type StatusFilterValue = typeof STATUS_FILTER_ALL | BackendStatus
export type PaymentModeFilterValue =
  | typeof PAYMENT_MODE_FILTER_ALL
  | PaymentMode
export type DatePreset = "7d" | "30d" | "90d" | "all"
export type UpdatedAtPreset = "all" | "this_week" | "7d" | "30d" | "90d"
export type PageSize = 5 | 10 | 20 | 50

export interface StatusFilterOption {
  readonly value: StatusFilterValue
  readonly label: string
}

export const STATUS_FILTER_OPTIONS: readonly StatusFilterOption[] = [
  { value: STATUS_FILTER_ALL, label: "All Status" },
  { value: "draft", label: "Draft" },
  { value: "sent", label: "Sent" },
  { value: "created", label: "Created" },
  { value: "viewed", label: "Viewed" },
  { value: "overdue", label: "Overdue" },
  { value: "paid", label: "Paid" },
  { value: "email_failed", label: "Email Failed" },
  { value: "payment_failed", label: "Payment Failed" },
  { value: "payment_pending", label: "Payment Pending" },
  { value: "expired", label: "Expired" },
  { value: "voided", label: "Voided" },
]

export interface PaymentModeFilterOption {
  readonly value: PaymentModeFilterValue
  readonly label: string
}

export const PAYMENT_MODE_FILTER_OPTIONS: readonly PaymentModeFilterOption[] = [
  { value: PAYMENT_MODE_FILTER_ALL, label: "All Modes" },
  { value: "full", label: "Full payment" },
  { value: "installments", label: "Installments" },
  { value: "deposit", label: "Deposit" },
]

export const DATE_PRESET_OPTIONS: readonly { value: DatePreset; label: string }[] = [
  { value: "7d", label: "Last 7 days" },
  { value: "30d", label: "Last 30 days" },
  { value: "90d", label: "Last 90 days" },
  { value: "all", label: "All time" },
]

export const PAGE_SIZE_OPTIONS: readonly PageSize[] = [5, 10, 20, 50]

const PRESET_DAYS: Record<Exclude<DatePreset, "all">, number> = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
}

export interface DateRange {
  created_at_from?: string
  created_at_to?: string
}

export function datePresetToRange(preset: DatePreset, now: Date): DateRange {
  if (preset === "all") return {}
  const days = PRESET_DAYS[preset]
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
  return { created_at_from: from.toISOString() }
}

export function parseStatusParam(raw: string | null): StatusFilterValue {
  if (raw === null || raw === "") return STATUS_FILTER_ALL
  const match = STATUS_FILTER_OPTIONS.find((opt) => opt.value === raw)
  return match ? match.value : STATUS_FILTER_ALL
}

export function parsePaymentModeParam(
  raw: string | null,
): PaymentModeFilterValue {
  if (raw === null || raw === "") return PAYMENT_MODE_FILTER_ALL
  const match = PAYMENT_MODE_FILTER_OPTIONS.find((opt) => opt.value === raw)
  return match ? match.value : PAYMENT_MODE_FILTER_ALL
}

/**
 * Read the market selection out of the *repeated* `market` query keys
 * (`?market=US&market=UK`). Values are trimmed, blanks dropped, and
 * duplicates removed with the first occurrence's position preserved. An
 * empty result means "all markets" (there is no sentinel).
 *
 * Values are passed through as free text rather than validated against a
 * known set: `market_name` is an upstream Planna snapshot, so an unmapped
 * label must stay filterable and a shared deep link must never be dropped.
 */
export function parseMarketParams(params: URLSearchParams): string[] {
  const seen = new Set<string>()
  const markets: string[] = []
  for (const raw of params.getAll("market")) {
    const value = raw.trim()
    if (value === "" || seen.has(value)) continue
    seen.add(value)
    markets.push(value)
  }
  return markets
}

/**
 * Read the currency filter selection. Lowercased to match the stored form;
 * absent/blank collapses to the "all" sentinel. An *unknown* three-letter
 * code is passed through (it renders as a seeded option and simply matches
 * no rows), but a value that isn't a three-letter code — the shape the API
 * accepts — collapses to "all" rather than being forwarded: the backend
 * 422s a malformed `currency`, so a stale/hand-crafted `?currency=usdd`
 * deep link would otherwise break the whole list instead of the picker
 * self-healing to "All".
 */
export function parseCurrencyParam(raw: string | null): CurrencyFilterValue {
  const value = (raw ?? "").trim().toLowerCase()
  return /^[a-z]{3}$/.test(value) ? value : CURRENCY_FILTER_ALL
}

/**
 * Append any selected value the option list doesn't carry, preserving the
 * option order. Both list-page pickers are fed by the facet feed, which
 * only reports values that currently exist — so a deep link shared before
 * the last invoice in that market/currency was voided would otherwise
 * render a control with no matching option (blank in a `Select`). Keeping
 * the orphan as an option keeps the link selected and still sent.
 */
export function withSeededOptions(
  options: string[],
  selected: string[],
): string[] {
  const orphaned = selected.filter((value) => !options.includes(value))
  return orphaned.length === 0 ? options : [...options, ...orphaned]
}

export function parseScopeParam(raw: string | null): ScopeFilter {
  if (raw === SCOPE_MINE) return SCOPE_MINE
  if (raw !== null && /^\d+$/.test(raw)) {
    const id = Number(raw)
    if (Number.isInteger(id) && id > 0) return id
  }
  return SCOPE_ALL
}

/**
 * The value the invoices API accepts on the ``created_by`` query
 * parameter. Exported so consumers of ``useInvoices`` can match the
 * picker's output shape exactly.
 */
export type CreatedByParam = number | typeof SCOPE_MINE

/**
 * Map an Owner-picker selection into the value sent to the invoices
 * API as ``?created_by=``. ``"all"`` collapses to ``undefined`` so
 * the parameter is omitted from the request entirely.
 */
export function scopeToCreatedByParam(
  value: ScopeFilter,
): CreatedByParam | undefined {
  if (value === SCOPE_ALL) return undefined
  if (value === SCOPE_MINE) return SCOPE_MINE
  return value
}

export function startOfCurrentWeek(now: Date): Date {
  const result = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    0,
    0,
    0,
    0,
  )
  const jsDay = result.getDay()
  const daysSinceMonday = jsDay === 0 ? 6 : jsDay - 1
  result.setDate(result.getDate() - daysSinceMonday)
  return result
}

export interface UpdatedAtRange {
  updated_at_from?: string
  updated_at_to?: string
}

export const UPDATED_AT_PRESET_OPTIONS: readonly {
  value: UpdatedAtPreset
  label: string
}[] = [
  { value: "all", label: "Updated: All time" },
  { value: "this_week", label: "Updated: This week" },
  { value: "7d", label: "Updated: Last 7 days" },
  { value: "30d", label: "Updated: Last 30 days" },
  { value: "90d", label: "Updated: Last 90 days" },
]

export function updatedAtPresetToRange(
  preset: UpdatedAtPreset,
  now: Date,
): UpdatedAtRange {
  if (preset === "all") return {}
  if (preset === "this_week") {
    return { updated_at_from: startOfCurrentWeek(now).toISOString() }
  }
  const days = PRESET_DAYS[preset]
  const from = new Date(now.getTime() - days * 24 * 60 * 60 * 1000)
  return { updated_at_from: from.toISOString() }
}

/**
 * Read the updated-at preset out of the compact ``updated`` URL token. The
 * token is the preset value itself (e.g. ``this_week``); ``all`` is the
 * default and is represented by the key's absence.
 *
 * Storing the preset token — rather than an absolute ``updated_at_from``
 * timestamp — keeps the URL time-stable: re-parsing minutes later (a
 * realistic list → detail → back gap) yields the same preset, where an
 * absolute instant would drift past a tolerance window and silently decay
 * to "All time". The API still receives absolute dates via
 * ``updatedAtPresetToRange``.
 */
export function parseUpdatedAtPresetToken(
  raw: string | null,
): UpdatedAtPreset {
  if (!raw) return "all"
  const match = UPDATED_AT_PRESET_OPTIONS.find((opt) => opt.value === raw)
  return match ? match.value : "all"
}

/** Serialize an updated-at preset to its URL token (undefined for "all"). */
export function updatedAtPresetToParam(
  preset: UpdatedAtPreset,
): string | undefined {
  return preset === "all" ? undefined : preset
}

/**
 * Parse the 1-based ``page`` query param. Anything non-integer, below 1, or
 * absent collapses to page 1 (the default, omitted from the URL).
 */
export function parsePageParam(raw: string | null): number {
  if (!raw) return 1
  const n = Number(raw)
  if (Number.isInteger(n) && n >= 1) return n
  return 1
}

export interface InvoiceListFilterState {
  searchText: string
  status: StatusFilterValue
  paymentMode: PaymentModeFilterValue
  datePreset: DatePreset
  courseId: typeof COURSE_FILTER_ALL | string
  pageSize: PageSize
  page: number
}

export const DEFAULT_FILTER_STATE: InvoiceListFilterState = {
  searchText: "",
  status: STATUS_FILTER_ALL,
  paymentMode: PAYMENT_MODE_FILTER_ALL,
  datePreset: "30d",
  courseId: COURSE_FILTER_ALL,
  pageSize: 20,
  page: 1,
}

export interface InvoiceQueryParams {
  status?: BackendStatus
  payment_mode?: PaymentMode
  course_id?: string
  search?: string
  created_at_from?: string
  created_at_to?: string
  limit: number
  offset: number
}

export function toInvoiceQueryParams(
  state: InvoiceListFilterState,
  now: Date = new Date(),
): InvoiceQueryParams {
  const params: InvoiceQueryParams = {
    limit: state.pageSize,
    offset: (state.page - 1) * state.pageSize,
  }

  if (state.status !== STATUS_FILTER_ALL) {
    params.status = state.status
  }
  if (state.paymentMode !== PAYMENT_MODE_FILTER_ALL) {
    params.payment_mode = state.paymentMode
  }
  if (state.courseId !== COURSE_FILTER_ALL) {
    params.course_id = state.courseId
  }
  const trimmedSearch = state.searchText.trim()
  if (trimmedSearch !== "") {
    params.search = trimmedSearch
  }
  const range = datePresetToRange(state.datePreset, now)
  if (range.created_at_from !== undefined) {
    params.created_at_from = range.created_at_from
  }
  if (range.created_at_to !== undefined) {
    params.created_at_to = range.created_at_to
  }
  return params
}

// ---------------------------------------------------------------------------
// URL-bound invoice-list filter state
//
// The invoice list surfaces (`/invoices` and the home "My Invoices"
// section) mirror the installments-failed-queue pattern: they own exactly
// the keys in `FILTER_URL_KEYS` and preserve every other query param when
// they rewrite the URL (deep-link anchors, analytics tags, future dev
// tweaks). The URL is the source of truth for the list state, so a
// list → detail → back round-trip restores the full filter + page
// selection. See features/installments-failed-queue/filters.ts.
// ---------------------------------------------------------------------------

/** The query keys owned by the invoice-list filters. */
export const FILTER_URL_KEYS = [
  "status",
  "payment_mode",
  "market",
  "currency",
  "created_by",
  "updated",
  "q",
  "page",
] as const

/**
 * The full URL-bound state of an invoice list surface. Every field maps to
 * exactly one owned query key; a field at its default value is omitted from
 * the serialized query string.
 */
export interface InvoiceListUrlState {
  status: StatusFilterValue
  paymentMode: PaymentModeFilterValue
  /** Multi-select; empty means "all markets" (design D9). */
  markets: string[]
  currency: CurrencyFilterValue
  scope: ScopeFilter
  updatedPreset: UpdatedAtPreset
  search: string
  page: number
}

export const DEFAULT_INVOICE_LIST_URL_STATE: InvoiceListUrlState = {
  status: STATUS_FILTER_ALL,
  paymentMode: PAYMENT_MODE_FILTER_ALL,
  markets: [],
  currency: CURRENCY_FILTER_ALL,
  scope: SCOPE_ALL,
  updatedPreset: "all",
  search: "",
  page: 1,
}

/**
 * Read the payment-mode filter selection out of a query string. Foreign
 * params are ignored; an unknown/absent value falls back to "all".
 */
export function parsePaymentModeFilterFromSearchParams(
  params: URLSearchParams,
): PaymentModeFilterValue {
  return parsePaymentModeParam(params.get("payment_mode"))
}

/**
 * Read the whole invoice-list filter state out of a query string. Foreign
 * params are ignored; each unknown/absent owned key falls back to its
 * default.
 */
export function parseInvoiceListState(
  params: URLSearchParams,
): InvoiceListUrlState {
  return {
    status: parseStatusParam(params.get("status")),
    paymentMode: parsePaymentModeParam(params.get("payment_mode")),
    markets: parseMarketParams(params),
    currency: parseCurrencyParam(params.get("currency")),
    scope: parseScopeParam(params.get("created_by")),
    updatedPreset: parseUpdatedAtPresetToken(params.get("updated")),
    // Trim so a raw `q=%20jane` deep link seeds the same value serialize
    // would emit — keeps parse ∘ serialize a fixed point (no mount rewrite).
    search: (params.get("q") ?? "").trim(),
    page: parsePageParam(params.get("page")),
  }
}

/**
 * Serialize just the owned keys, omitting any field at its default value so
 * that `parse ∘ serialize` is idempotent and clean links stay clean. The
 * search text is trimmed to match the value actually queried.
 */
export function serializeInvoiceListState(
  state: InvoiceListUrlState,
): URLSearchParams {
  const search = new URLSearchParams()
  if (state.status !== STATUS_FILTER_ALL) search.set("status", state.status)
  if (state.paymentMode !== PAYMENT_MODE_FILTER_ALL) {
    search.set("payment_mode", state.paymentMode)
  }
  // The one repeated key: one `market` entry per selection, which is
  // exactly the shape the API accepts (design D8/R5). `append`, not `set`,
  // or a multi-market selection would collapse to its last value.
  for (const market of state.markets) search.append("market", market)
  if (state.currency !== CURRENCY_FILTER_ALL) {
    search.set("currency", state.currency)
  }
  const createdBy = scopeToCreatedByParam(state.scope)
  if (createdBy !== undefined) search.set("created_by", String(createdBy))
  const updated = updatedAtPresetToParam(state.updatedPreset)
  if (updated !== undefined) search.set("updated", updated)
  const trimmed = state.search.trim()
  if (trimmed !== "") search.set("q", trimmed)
  if (state.page > 1) search.set("page", String(state.page))
  return search
}

/**
 * Overwrite the owned keys of `current` with `owned`, preserving every
 * foreign key. The single chokepoint both list surfaces (and the URL-sync
 * hook) route through, so "rewrite only what we own" is defined once.
 *
 * Owned values are `append`ed after every owned key is deleted, so a
 * repeated key (`market`) survives intact rather than collapsing to its
 * last value; for single-valued keys this is identical to `set`.
 */
export function mergeOwnedIntoSearchParams(
  current: URLSearchParams,
  ownedKeys: readonly string[],
  owned: URLSearchParams,
): URLSearchParams {
  const merged = new URLSearchParams(current)
  for (const key of ownedKeys) merged.delete(key)
  for (const [key, val] of owned) merged.append(key, val)
  return merged
}

// ---------------------------------------------------------------------------
// URL-bound home "My Invoices" filter state
//
// The home section owns a different, richer filter set than `/invoices` — a
// created-at window (`created`), a course filter (`course`), and a
// selectable page size (`page_size`), but no owner scope (it is pinned to
// the signed-in user). It therefore has its own owned-key set and
// serializer, sharing only the primitive parsers and the merge chokepoint.
// ---------------------------------------------------------------------------

/** The query keys owned by the home "My Invoices" filters. */
export const HOME_FILTER_URL_KEYS = [
  "status",
  "payment_mode",
  "created",
  "course",
  "q",
  "page",
  "page_size",
] as const

/**
 * Read the created-at window out of the compact `created` URL token. The
 * token is the preset value itself (e.g. `7d`); the home default (`30d`) is
 * represented by the key's absence.
 */
export function parseDatePresetToken(raw: string | null): DatePreset {
  if (!raw) return DEFAULT_FILTER_STATE.datePreset
  const match = DATE_PRESET_OPTIONS.find((opt) => opt.value === raw)
  return match ? match.value : DEFAULT_FILTER_STATE.datePreset
}

/** Parse the `page_size` query param, falling back to the home default. */
export function parsePageSizeParam(raw: string | null): PageSize {
  if (!raw) return DEFAULT_FILTER_STATE.pageSize
  const n = Number(raw)
  const match = PAGE_SIZE_OPTIONS.find((size) => size === n)
  return match ?? DEFAULT_FILTER_STATE.pageSize
}

/**
 * Read the whole home filter state out of a query string. Foreign params are
 * ignored; each unknown/absent owned key falls back to its home default.
 */
export function parseHomeInvoiceListState(
  params: URLSearchParams,
): InvoiceListFilterState {
  return {
    searchText: (params.get("q") ?? "").trim(),
    status: parseStatusParam(params.get("status")),
    paymentMode: parsePaymentModeParam(params.get("payment_mode")),
    datePreset: parseDatePresetToken(params.get("created")),
    // `|| ALL` (not `??`) so a bare `course=` collapses to the "all" sentinel
    // instead of an empty-string course id that would leak to the API.
    courseId: params.get("course") || COURSE_FILTER_ALL,
    pageSize: parsePageSizeParam(params.get("page_size")),
    page: parsePageParam(params.get("page")),
  }
}

/**
 * Serialize just the owned home keys, omitting any field at its default so
 * `parse ∘ serialize` is idempotent and clean links stay clean.
 */
export function serializeHomeInvoiceListState(
  state: InvoiceListFilterState,
): URLSearchParams {
  const search = new URLSearchParams()
  if (state.status !== STATUS_FILTER_ALL) search.set("status", state.status)
  if (state.paymentMode !== PAYMENT_MODE_FILTER_ALL) {
    search.set("payment_mode", state.paymentMode)
  }
  if (state.datePreset !== DEFAULT_FILTER_STATE.datePreset) {
    search.set("created", state.datePreset)
  }
  if (state.courseId !== COURSE_FILTER_ALL) search.set("course", state.courseId)
  const trimmed = state.searchText.trim()
  if (trimmed !== "") search.set("q", trimmed)
  if (state.page > 1) search.set("page", String(state.page))
  if (state.pageSize !== DEFAULT_FILTER_STATE.pageSize) {
    search.set("page_size", String(state.pageSize))
  }
  return search
}
